import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListIcon,
  Flex,
  Text,
  Divider,
  Image,
} from "@chakra-ui/react";
import { Link, useLocation } from "react-router-dom";
import { Link as ChakraLink } from "@chakra-ui/react";
import {
  FaTachometerAlt,
  FaChartBar,
  FaInbox,
  FaCommentDots,
  FaEnvelope,
  FaUsers,
  FaUserShield,
  FaNewspaper,
  FaUpload,
  FaArrowLeft,
} from "react-icons/fa";
import logo from "../../assets/images/logo1.png";
import axiosInstance from "../../helpers/axiosInstance";
import { getNotificationCounts } from "../../api/problemReports";
import { useOTP } from "../AdminPanelAccess/OTPContext";

const Sidebar = () => {
  const location = useLocation();
  const { isOTPVerified } = useOTP();

  /* -------------------------------- NAV LINKS ------------------------------ */
  const navLinks = [
    { to: "/admin/dashboard", text: "Dashboard", icon: FaTachometerAlt },
    { to: "/admin/analytics", text: "Analytics", icon: FaChartBar },
    { to: "/admin/mailbox", text: "Helpdesk / CRM", icon: FaInbox },
    { to: "/admin/feedback", text: "Feedback Surveys", icon: FaCommentDots },
    { to: "/admin/newsletters", text: "Newsletters", icon: FaEnvelope },
    { to: "/admin/filter", text: "Users", icon: FaUsers },
    {
      to: "/admin/user-management",
      text: "Role Management",
      icon: FaUserShield,
    },
    { to: "/admin/all-blog", text: "All Blogs", icon: FaNewspaper },
    { to: "/admin/blog/upload", text: "Upload Blogs", icon: FaUpload },
    { to: "/admin/notifications", text: "Notification", icon: FaEnvelope },
  ];

  /* ------------------------------ NOTIFICATION COUNTS ---------------------------- */
  const [notificationCounts, setNotificationCounts] = useState({
    unresponded_problems: 0,
    unresponded_applications: 0,
    unread_emails: 0,
    total_notifications: 0,
  });

  useEffect(() => {
    const fetchNotificationCounts = async () => {
      try {
        const response = await getNotificationCounts();
        if (response.success) {
          setNotificationCounts(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch notification counts:", err);
      }
    };

    fetchNotificationCounts();
  }, []);

  /* Paths that should highlight "Helpdesk / CRM" */
  const helpdeskPaths = [
    "/admin/mailbox",
    "/admin/userform",
    "/admin/report/problem", // ← new
  ];

  // Don't render sidebar if OTP is not verified
  if (!isOTPVerified) {
    return null;
  }

  return (
    <Box w="20vw" h="100vh" bg="gray.900" color="white" pt={4} boxShadow="md">
      {/* Logo */}
      <Link to="/home">
        <Flex align="center" fontSize="xl" fontWeight="bold" mb={4} pl={2}>
          <Image src={logo} height="24px" display="block" />
          <Text
            ml={2}
            fontSize="20px"
            lineHeight="24px"
            display="flex"
            alignItems="center"
            position="relative"
            top="8px" 
          >
            NOMAD360
          </Text>
        </Flex>
      </Link>

      {/* Navigation */}
      <List spacing={3} pl={0}>
        {navLinks.map((link) => {
          const isHelpdesk = link.text === "Helpdesk / CRM";
          const isActive =
            location.pathname === link.to ||
            (isHelpdesk && helpdeskPaths.includes(location.pathname));

          return (
            <ListItem
              key={link.to}
              borderRadius="md"
              bg={isActive ? "#286328" : "transparent"}
              _hover={isActive ? {} : { bg: "gray.700", color: "white" }}
              p={1}
              mx={2}
            >
              <ChakraLink
                as={Link}
                to={link.to}
                style={{ textDecoration: "none", width: "100%" }}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Flex align="center">
                  <ListIcon as={link.icon} color="white" boxSize={4} />
                  <Text m={0} color={isActive ? "white" : "gray.300"}>
                    {link.text}
                  </Text>

                  {/* Badge for Helpdesk / CRM using total_notifications */}
                  {isHelpdesk && notificationCounts.total_notifications > 0 && (
                    <Flex
                      bg="red.500"
                      color="white"
                      borderRadius="full"
                      h={4}
                      w={4}
                      justify="center"
                      align="center"
                      ml={2}
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      {notificationCounts.total_notifications}
                    </Flex>
                  )}
                </Flex>
              </ChakraLink>
            </ListItem>
          );
        })}

        {/* Divider */}
        <ListItem px={2}>
          <Divider borderColor="gray.600" />
        </ListItem>

        {/* Back to App */}
        <ListItem
          key="/ai-travel-planner"
          borderRadius="md"
          bg="transparent"
          _hover={{ bg: "gray.700", color: "white" }}
          p={1}
          mx={2}
        >
          <ChakraLink
            as={Link}
            to="/ai-travel-planner"
            style={{ textDecoration: "none", width: "100%" }}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <Flex align="center">
              <ListIcon as={FaArrowLeft} color="white" boxSize={4} />
              <Text m={0} color="gray.300">
                Back to Application
              </Text>
            </Flex>
          </ChakraLink>
        </ListItem>
      </List>
    </Box>
  );
};

export default Sidebar;
